import { Button } from '@app/components/various/Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '@app/components/various/Dropdown';
import { Download } from '@app/components/various/Icons';
import { Skeleton } from '@app/components/various/Skeleton';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { useAmplitude, useIsScreenSize } from '@app/utils/hooks';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { Dropdown as DropodownType } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { DownloadProductInformationModal } from '../DownloadProductInformationModal';

interface Props {
  onDownloadLineseet: VoidFunction;
  onDownloadOrderExcel: VoidFunction;
  title: string;
  name?: string;
  isLoading: boolean;
}

export const OrderDetailsHeader = ({ onDownloadOrderExcel, onDownloadLineseet, title, name, isLoading }: Props) => {
  const { t } = useTranslation(['common', 'linesheets', 'checkout']);
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const isProductInfoConfiguratorEnabled = getIsFeatureEnabled('ENABLE_PRODUCT_INFO_FILE_CONFIG');
  const { showModal: showProductInfoModal, hideModal: hideProductInfoModal, isOpen: isProductInfoModalOpen } = useModalToggle();
  const { trackEvent } = useAmplitude();

  const menuItems: DropodownType.MenuItem[] = [
    {
      key: 'downloadExcel',
      label: t('checkout:download.order_details', { extension: 'xls' }),
      onClick: onDownloadOrderExcel,
    },
    {
      key: 'downloadLinesheet',
      label: t('linesheets:generate_linesheet'),
      onClick: onDownloadLineseet,
    },
    ...(isProductInfoConfiguratorEnabled ?
      [
        {
          key: 'productInfoXls',
          label: t('checkout:download.product_info', { extension: 'xls' }),
          onClick: showProductInfoModal,
        },
      ]
    : []),
  ];

  const handleSuccessProductInfoModal = React.useCallback(() => {
    trackEvent({ name: 'downloadAction.button.productInformationXls.submit', properties: { path: 'mySelection' } });
  }, [trackEvent]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-between pb-5 sm:pt-4 lg:pt-8">
        <Skeleton type="text" itemClassName="w-48" size={isMobile ? 'large' : 'xxlarge'} />
        <div className="w-14">
          <Skeleton type="button" size={isMobile ? 'medium' : 'large'} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 pb-5 sm:pt-4 lg:pt-8">
      <div className="flex items-center justify-between">
        <h2 className="heading-2 sm:heading-1">{title}</h2>
        <Dropdown title={t('common:download')} placement="bottom-end">
          <DropdownTrigger>
            {({ isOpen: isDropdownOpen, triggerProps }) => (
              <Button
                isChevronOpen={isDropdownOpen}
                icon={Download}
                variant="bordered"
                color="secondary"
                size={isMobile ? 'medium' : 'large'}
                {...triggerProps}
              />
            )}
          </DropdownTrigger>
          <DropdownMenu items={menuItems} />
        </Dropdown>
        {isProductInfoConfiguratorEnabled && (
          <DownloadProductInformationModal
            isOpen={isProductInfoModalOpen}
            onClose={hideProductInfoModal}
            onSuccess={handleSuccessProductInfoModal}
          />
        )}
      </div>
      <span className="text-m text-neutral-20">{name}</span>
    </div>
  );
};
